import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, CircleAlert, Cpu, HardDrive, MonitorUp, Network, Server } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCredentials } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import { selectDefaultTemplateId } from "../../domain/template-selection";
import { createVps, errorMessage, getBilling, getTemplates } from "../../lib/api";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";

export function CreateVpsModal({ onClose }: { onClose: () => void }) {
  const credentials = useCredentials();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [templateId, setTemplateId] = useState("");
  const selectedTemplateRef = useRef<HTMLLabelElement>(null);
  const [vcpus, setVcpus] = useState(1);
  const [vram, setVram] = useState(1);
  const [disk, setDisk] = useState(6);
  const [publicIpv4, setPublicIpv4] = useState(true);
  const [privateIpv4, setPrivateIpv4] = useState(false);
  const templates = useQuery({ queryKey: ["templates", credentials.login], queryFn: () => getTemplates(credentials) });
  const billing = useQuery({ queryKey: ["billing", credentials.login], queryFn: () => getBilling(credentials) });
  const qcow2Enabled = billing.data?.capabilities.qcow2 === true;
  const selected = useMemo(() => templates.data?.find((template) => template.id === templateId), [templateId, templates.data]);
  const isIsoTemplate = selected?.type.trim().toUpperCase() === "ISO";

  useEffect(() => {
    if (!templateId && templates.data && billing.data) {
      const defaultTemplateId = selectDefaultTemplateId(templates.data, qcow2Enabled);
      if (defaultTemplateId) setTemplateId(defaultTemplateId);
    }
  }, [billing.data, qcow2Enabled, templateId, templates.data]);
  useEffect(() => {
    if (step !== 1 || !templateId) return;
    const frame = requestAnimationFrame(() => selectedTemplateRef.current?.scrollIntoView({ block: "nearest", behavior: "auto" }));
    return () => cancelAnimationFrame(frame);
  }, [step, templateId]);
  useEffect(() => {
    if (!selected) return;
    setVcpus((value) => Math.max(value, selected.minCpuCores));
    setVram((value) => Math.max(value, selected.minRamGiB));
    setDisk((value) => Math.max(value, selected.minDiskGiB));
  }, [selected]);

  const mutation = useMutation({
    mutationFn: () => createVps(credentials, { template: templateId, vcpus, vramGiB: vram, storageGiB: disk, publicIpv4, privateIpv4 }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["nodes", credentials.login] });
      if (result.status === 201 || result.status === 202) notify(result.description ?? "VPS byl vytvořen, ale některá IPv4 nebyla dostupná.", "info");
      else notify("Požadavek na vytvoření VPS byl přijat.", "success");
      onClose();
    },
  });

  const stepNames = ["Šablona", "Výkon", "Síť", "Rekapitulace"];
  const footer = (
    <>
      <Button variant="ghost" onClick={step === 1 ? onClose : () => setStep((current) => current - 1)} icon={step > 1 ? <ArrowLeft /> : undefined}>{step === 1 ? "Zrušit" : "Zpět"}</Button>
      {step < 4 ? <Button variant="primary" onClick={() => setStep((current) => current + 1)} disabled={!templateId} icon={<ArrowRight />}>Pokračovat</Button> : <Button variant="primary" onClick={() => mutation.mutate()} busy={mutation.isPending} icon={<Server />}>Vytvořit VPS</Button>}
    </>
  );

  return (
    <Modal title="Nový virtuální server" description={`Krok ${step} ze 4 · ${stepNames[step - 1]}`} onClose={onClose} footer={footer}>
      <div className="wizard-progress">{stepNames.map((name, index) => <div key={name} className={step > index ? "active" : ""}><span>{step > index + 1 ? <Check /> : index + 1}</span><small>{name}</small></div>)}</div>
      {(templates.isLoading || billing.isLoading) && <div className="loading-panel"><span className="spinner" /> Načítám instalační zdroje…</div>}
      {(templates.error || billing.error) && <div className="form-error">{errorMessage(templates.error ?? billing.error)}</div>}
      {step === 1 && templates.data && billing.data && (
        <>
          {!qcow2Enabled && <div className="template-verification-notice" role="note"><CircleAlert /><div><strong>Hotové instalace zpřístupníme po kontrole fakturačních údajů</strong><p>Do té doby můžete vytvořit VPS z instalačního ISO. QCOW2 šablony zůstávají v seznamu viditelné, ale nelze je vybrat.</p></div></div>}
          <div className="template-list">
            {templates.data.map((template) => {
              const qcow2 = template.type.trim().toUpperCase() === "QCOW2";
              const disabled = qcow2 && !qcow2Enabled;
              return (
                <label key={template.id} ref={templateId === template.id ? selectedTemplateRef : undefined} aria-disabled={disabled} title={disabled ? "Dostupné po ověření fakturačních údajů" : undefined} className={`template-option ${templateId === template.id ? "selected" : ""} ${disabled ? "template-option--disabled" : ""}`}>
                  <input type="radio" name="template" value={template.id} checked={templateId === template.id} disabled={disabled} onChange={() => setTemplateId(template.id)} />
                  <span className="template-option__icon"><Server /></span>
                  <span><strong>{template.id}</strong><small>{template.description || `${template.type} instalační zdroj`}</small><em>{template.type} · min. {template.minCpuCores} vCPU / {template.minRamGiB} GiB RAM{disabled ? " · po ověření údajů" : ""}</em></span>
                  <i>{templateId === template.id && <Check />}</i>
                </label>
              );
            })}
          </div>
        </>
      )}
      {step === 2 && (
        <div className="config-grid">
          <label className="field"><span><Cpu /> Počet vCPU</span><select value={vcpus} onChange={(event) => setVcpus(Number(event.target.value))}>{Array.from({ length: 8 - (selected?.minCpuCores ?? 1) + 1 }, (_, index) => index + (selected?.minCpuCores ?? 1)).map((value) => <option key={value} value={value}>{value} {value === 1 ? "jádro" : "jader"}</option>)}</select></label>
          <label className="field"><span><Cpu /> Velikost vRAM</span><select value={vram} onChange={(event) => setVram(Number(event.target.value))}>{Array.from({ length: 32 - (selected?.minRamGiB ?? 1) + 1 }, (_, index) => index + (selected?.minRamGiB ?? 1)).map((value) => <option key={value} value={value}>{value} GiB</option>)}</select></label>
          <label className="field config-grid__wide"><span><HardDrive /> Diskový prostor</span><div className="range-field"><input type="range" min={selected?.minDiskGiB ?? 1} max={100} value={disk} disabled={selected?.type === "QCOW2"} onChange={(event) => setDisk(Number(event.target.value))} /><strong>{disk} GiB</strong></div>{selected?.type === "QCOW2" && <small>Velikost disku je daná zvolenou hotovou šablonou.</small>}</label>
        </div>
      )}
      {step === 3 && (
        <div className="network-choice">
          <label className={publicIpv4 ? "selected" : ""}><input type="checkbox" checked={publicIpv4} onChange={(event) => setPublicIpv4(event.target.checked)} /><span><GlobeIcon /><strong>Veřejná IPv4</strong><small>Přímá dostupnost VPS z internetu. Zpoplatněná dle ceníku.</small></span><i>{publicIpv4 && <Check />}</i></label>
          <label className={privateIpv4 ? "selected" : ""}><input type="checkbox" checked={privateIpv4} onChange={(event) => setPrivateIpv4(event.target.checked)} /><span><Network /><strong>Privátní IPv4</strong><small>Komunikace v interní síti 4smart.cz a možnost NAT.</small></span><i>{privateIpv4 && <Check />}</i></label>
          <div className="info-box"><Network /><p>IPv6 síťové nastavení je součástí VPS. Další adresy můžete přidat později v detailu serveru.</p></div>
        </div>
      )}
      {step === 4 && (
        <div className="review-card">
          <div><span className="review-card__icon"><Server /></span><span><small>Instalační zdroj</small><strong>{selected?.id}</strong></span></div>
          <dl><div><dt>vCPU</dt><dd>{vcpus} jader</dd></div><div><dt>vRAM</dt><dd>{vram} GiB</dd></div><div><dt>Disk</dt><dd>{disk} GiB</dd></div><div><dt>Veřejná IPv4</dt><dd>{publicIpv4 ? "Ano" : "Ne"}</dd></div><div><dt>Privátní IPv4</dt><dd>{privateIpv4 ? "Ano" : "Ne"}</dd></div><div><dt>Autostart a VNC</dt><dd>Zapnuto</dd></div></dl>
          <div className="info-box"><Server /><p>Vytvoření probíhá na pozadí a může několik minut trvat. Během této doby budou další operace nad VPS zablokované.</p></div>
          {isIsoTemplate && <div className="iso-install-notice" role="note"><MonitorUp /><p>Po vytvoření VPS pokračujte jeho konfigurací s pomocí aplikace pro práci se vzdálenou plochou VNC.</p></div>}
          {mutation.error && <div className="form-error">{errorMessage(mutation.error)}</div>}
        </div>
      )}
    </Modal>
  );
}

function GlobeIcon() {
  return <Network />;
}
